import { useState, useRef, useCallback, useEffect, type ReactNode } from 'react'
import styles from './FAB.module.css'

interface FABProps {
  children: ReactNode
  onClick?: () => void
}

const SIZE = 56
const PADDING = 16

function snapToCorner(x: number, y: number) {
  const vw = window.innerWidth
  const vh = window.innerHeight
  // 动态获取底部导航栏高度
  const bottomNav = document.querySelector('[class*="bottomNav"], [class*="BottomNav"], nav')
  const btnBottom = bottomNav ? bottomNav.getBoundingClientRect().height + 16 : 90

  const corners = [
    { x: PADDING, y: vh - SIZE - btnBottom },                     // 左下
    { x: vw - SIZE - PADDING, y: vh - SIZE - btnBottom },         // 右下
    { x: PADDING, y: PADDING + 56 },                              // 左上（避开顶栏）
    { x: vw - SIZE - PADDING, y: PADDING + 56 },                  // 右上
  ]

  let best = corners[0]
  let bestDist = Infinity
  for (const c of corners) {
    const d = Math.hypot(x - c.x, y - c.y)
    if (d < bestDist) { bestDist = d; best = c }
  }
  return best
}

export default function FAB({ children, onClick }: FABProps) {
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null)
  const [snapped, setSnapped] = useState<{ x: number; y: number }>(() => {
    const saved = localStorage.getItem('fab_pos')
    if (saved) return JSON.parse(saved)
    return { x: window.innerWidth - SIZE - PADDING, y: window.innerHeight - SIZE - 90 }
  })

  const dragging = useRef(false)
  const startX = useRef(0)
  const startY = useRef(0)
  const startPos = useRef({ x: 0, y: 0 })
  const moved = useRef(false)
  const buttonRef = useRef<HTMLButtonElement>(null)

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    dragging.current = true
    moved.current = false
    startX.current = e.clientX
    startY.current = e.clientY
    const rect = (e.target as HTMLElement).getBoundingClientRect()
    startPos.current = { x: rect.left, y: rect.top }
    buttonRef.current?.setPointerCapture(e.pointerId)
  }, [])

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragging.current) return
    const dx = e.clientX - startX.current
    const dy = e.clientY - startY.current
    if (Math.abs(dx) > 8 || Math.abs(dy) > 8) moved.current = true
    if (moved.current) {
      const newX = Math.max(0, Math.min(window.innerWidth - SIZE, startPos.current.x + dx))
      const newY = Math.max(0, Math.min(window.innerHeight - SIZE, startPos.current.y + dy))
      setPos({ x: newX, y: newY })
    }
  }, [])

  const handlePointerUp = useCallback(() => {
    if (!dragging.current) return
    dragging.current = false
    if (moved.current && pos) {
      const corner = snapToCorner(pos.x, pos.y)
      setSnapped(corner)
      localStorage.setItem('fab_pos', JSON.stringify(corner))
    }
    setPos(null)
  }, [pos])

  const handleClick = useCallback(() => {
    if (moved.current) return
    onClick?.()
  }, [onClick])

  // 窗口缩放时重新吸附到最近角落
  useEffect(() => {
    const handleResize = () => {
      setSnapped(prev => {
        const corner = snapToCorner(prev.x, prev.y)
        localStorage.setItem('fab_pos', JSON.stringify(corner))
        return corner
      })
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const style: React.CSSProperties = pos
    ? { left: pos.x, top: pos.y, position: 'fixed' as const }
    : { left: snapped.x, top: snapped.y, position: 'fixed' as const }

  return (
    <button
      ref={buttonRef}
      className={`${styles.fab} ${pos ? styles.fabDragging : ''}`}
      style={style}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={() => { dragging.current = false; setPos(null) }}
      onClick={handleClick}
    >
      {children}
    </button>
  )
}
